/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  View,
  Text,
  SafeAreaView,
  StatusBar,
  FlatList,
  Alert,
  TextInput,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';

const LearningCoursesScreen = ({ navigation }) => {
  const [activeTab, setActiveTab] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('title'); // title, rating, price, duration

  const categories = [
    { id: 'All', name: 'All' },
    { id: 'Mechanical', name: 'Mechanical' },
    { id: 'Civil', name: 'Civil' },
    { id: 'Electrical', name: 'Electrical' },
    { id: 'Computer', name: 'Computer' },
    { id: 'Chemical', name: 'Chemical' },
    { id: 'Materials', name: 'Materials' },
    { id: 'Environmental', name: 'Environmental' },
    { id: 'Biomedical', name: 'Biomedical' },
    { id: 'Aerospace', name: 'Aerospace' },
  ];

  const courses = [
    // Free Courses
    {
      id: '1',
      title: 'Introduction to Python for Engineers',
      subtitle: 'Programming fundamentals for engineering applications',
      instructor: 'Prof. Sarah Johnson',
      duration: '8 hours',
      rating: 4.8,
      students: 18500,
      level: 'Beginner',
      isFree: true,
      price: 0,
      color: '#3776AB',
      icon: 'language-python',
      progress: 0,
      category: 'Computer',
      description: 'Learn Python programming fundamentals with engineering applications. Covers data analysis, automation, and scientific computing.',
      modules: [
        { id: '1', title: 'Python Basics and Syntax', duration: '2 hours' },
        { id: '2', title: 'Data Structures for Engineering', duration: '2 hours' },
        { id: '3', title: 'NumPy and Scientific Computing', duration: '2 hours' },
        { id: '4', title: 'Engineering Applications', duration: '2 hours' },
      ],
    },
    {
      id: '2',
      title: 'CAD Fundamentals with Fusion 360',
      subtitle: 'Learn 3D modeling and design basics',
      instructor: 'Autodesk Certified Instructor',
      duration: '6 hours',
      rating: 4.7,
      students: 12500,
      level: 'Beginner',
      isFree: true,
      price: 0,
      color: '#FF6B35',
      icon: 'cube',
      progress: 0,
      category: 'Mechanical',
      description: 'Master the basics of CAD design using Autodesk Fusion 360. Perfect for mechanical and product design students.',
      modules: [
        { id: '1', title: 'Interface and Sketching', duration: '1.5 hours' },
        { id: '2', title: '3D Modeling Basics', duration: '1.5 hours' },
        { id: '3', title: 'Assembly Design', duration: '1.5 hours' },
        { id: '4', title: 'Basic Rendering', duration: '1.5 hours' },
      ],
    },
    {
      id: '3',
      title: 'Circuit Analysis Fundamentals',
      subtitle: 'Basic electrical circuit concepts',
      instructor: 'Dr. Michael Chen',
      duration: '10 hours',
      rating: 4.6,
      students: 9800,
      level: 'Beginner',
      isFree: true,
      price: 0,
      color: '#FFD700',
      icon: 'flash',
      progress: 0,
      category: 'Electrical',
      description: 'Learn the fundamental principles of electrical circuits including Ohm\'s Law, Kirchhoff\'s Laws, and basic circuit analysis.',
      modules: [
        { id: '1', title: 'Basic Components and Laws', duration: '2.5 hours' },
        { id: '2', title: 'Series and Parallel Circuits', duration: '2.5 hours' },
        { id: '3', title: 'AC Circuit Fundamentals', duration: '2.5 hours' },
        { id: '4', title: 'Practical Applications', duration: '2.5 hours' },
      ],
    },
    {
      id: '4',
      title: 'Sustainable Engineering Basics',
      subtitle: 'Introduction to green technologies',
      instructor: 'Dr. Emily Green',
      duration: '7 hours',
      rating: 4.5,
      students: 8500,
      level: 'Beginner',
      isFree: true,
      price: 0,
      color: '#4CAF50',
      icon: 'leaf',
      progress: 0,
      category: 'Environmental',
      description: 'Explore sustainable engineering practices, renewable energy systems, and environmental impact assessment.',
      modules: [
        { id: '1', title: 'Principles of Sustainability', duration: '2 hours' },
        { id: '2', title: 'Renewable Energy Systems', duration: '2 hours' },
        { id: '3', title: 'Green Building Design', duration: '2 hours' },
        { id: '4', title: 'Case Studies', duration: '1 hour' },
      ],
    },

    // Paid Courses (Intermediate to Advanced)
    {
      id: '5',
      title: 'Advanced Structural Analysis',
      subtitle: 'Finite element methods and dynamic analysis',
      instructor: 'Dr. Robert Williams',
      duration: '25 hours',
      rating: 4.9,
      students: 3200,
      level: 'Advanced',
      isFree: false,
      price: 149,
      color: '#0e4e96',
      icon: 'bridge',
      progress: 0,
      category: 'Civil',
      description: 'Master advanced structural analysis techniques including finite element methods, dynamic analysis, and seismic design.',
      modules: [
        { id: '1', title: 'FEM Fundamentals', duration: '5 hours' },
        { id: '2', title: 'Dynamic Analysis', duration: '6 hours' },
        { id: '3', title: 'Seismic Design Principles', duration: '7 hours' },
        { id: '4', title: 'Advanced Applications', duration: '7 hours' },
      ],
    },
    {
      id: '6',
      title: 'Machine Learning for Engineers',
      subtitle: 'AI applications in engineering systems',
      instructor: 'Dr. Alex Rodriguez',
      duration: '30 hours',
      rating: 4.8,
      students: 4500,
      level: 'Intermediate',
      isFree: false,
      price: 199,
      color: '#FF4081',
      icon: 'robot',
      progress: 0,
      category: 'Computer',
      description: 'Apply machine learning techniques to solve complex engineering problems including predictive maintenance and optimization.',
      modules: [
        { id: '1', title: 'ML Fundamentals for Engineers', duration: '6 hours' },
        { id: '2', title: 'Data Preprocessing', duration: '6 hours' },
        { id: '3', title: 'Predictive Modeling', duration: '9 hours' },
        { id: '4', title: 'Engineering Case Studies', duration: '9 hours' },
      ],
    },
    {
      id: '7',
      title: 'HVAC System Design',
      subtitle: 'Complete heating, ventilation, and air conditioning design',
      instructor: 'PE. James Miller',
      duration: '22 hours',
      rating: 4.7,
      students: 2800,
      level: 'Intermediate',
      isFree: false,
      price: 129,
      color: '#2196F3',
      icon: 'air-conditioner',
      progress: 0,
      category: 'Mechanical',
      description: 'Learn professional HVAC system design including load calculations, duct design, and equipment selection.',
      modules: [
        { id: '1', title: 'Heat Load Calculations', duration: '6 hours' },
        { id: '2', title: 'Duct System Design', duration: '5 hours' },
        { id: '3', title: 'Equipment Selection', duration: '5 hours' },
        { id: '4', title: 'Energy Efficiency', duration: '6 hours' },
      ],
    },
    {
      id: '8',
      title: 'Power Electronics',
      subtitle: 'Design and analysis of power electronic circuits',
      instructor: 'Dr. Lisa Wang',
      duration: '28 hours',
      rating: 4.9,
      students: 3100,
      level: 'Advanced',
      isFree: false,
      price: 179,
      color: '#9C27B0',
      icon: 'transmission-tower',
      progress: 0,
      category: 'Electrical',
      description: 'Master power electronics including converter design, motor drives, and renewable energy applications.',
      modules: [
        { id: '1', title: 'Power Semiconductor Devices', duration: '6 hours' },
        { id: '2', title: 'DC-DC Converters', duration: '7 hours' },
        { id: '3', title: 'Motor Drives', duration: '8 hours' },
        { id: '4', title: 'Renewable Energy Systems', duration: '7 hours' },
      ],
    },
    {
      id: '9',
      title: 'Chemical Process Simulation',
      subtitle: 'Aspen HYSYS and process optimization',
      instructor: 'Dr. Maria Garcia',
      duration: '35 hours',
      rating: 4.8,
      students: 1900,
      level: 'Advanced',
      isFree: false,
      price: 249,
      color: '#FF5722',
      icon: 'flask',
      progress: 0,
      category: 'Chemical',
      description: 'Learn professional chemical process simulation using Aspen HYSYS for process design and optimization.',
      modules: [
        { id: '1', title: 'HYSYS Fundamentals', duration: '8 hours' },
        { id: '2', title: 'Process Simulation', duration: '9 hours' },
        { id: '3', title: 'Optimization Techniques', duration: '9 hours' },
        { id: '4', title: 'Industry Case Studies', duration: '9 hours' },
      ],
    },
    {
      id: '10',
      title: 'Biomedical Instrumentation',
      subtitle: 'Medical device design and development',
      instructor: 'Dr. Thomas Lee',
      duration: '26 hours',
      rating: 4.7,
      students: 2200,
      level: 'Intermediate',
      isFree: false,
      price: 169,
      color: '#E91E63',
      icon: 'heart-pulse',
      progress: 0,
      category: 'Biomedical',
      description: 'Design and develop biomedical instruments including sensors, monitoring devices, and diagnostic equipment.',
      modules: [
        { id: '1', title: 'Biomedical Sensors', duration: '6 hours' },
        { id: '2', title: 'Signal Processing', duration: '7 hours' },
        { id: '3', title: 'Medical Device Standards', duration: '6 hours' },
        { id: '4', title: 'Design Projects', duration: '7 hours' },
      ],
    },
    {
      id: '11',
      title: 'Aerodynamics and Flight',
      subtitle: 'Principles of aircraft design and performance',
      instructor: 'Captain David Wilson',
      duration: '32 hours',
      rating: 4.9,
      students: 1800,
      level: 'Advanced',
      isFree: false,
      price: 229,
      color: '#3F51B5',
      icon: 'airplane',
      progress: 0,
      category: 'Aerospace',
      description: 'Comprehensive study of aerodynamics, flight mechanics, and aircraft performance analysis.',
      modules: [
        { id: '1', title: 'Fundamental Aerodynamics', duration: '8 hours' },
        { id: '2', title: 'Flight Performance', duration: '8 hours' },
        { id: '3', title: 'Stability and Control', duration: '8 hours' },
        { id: '4', title: 'Aircraft Design Principles', duration: '8 hours' },
      ],
    },
    {
      id: '12',
      title: 'Materials Engineering',
      subtitle: 'Advanced material selection and processing',
      instructor: 'Dr. Jennifer Smith',
      duration: '24 hours',
      rating: 4.6,
      students: 2500,
      level: 'Intermediate',
      isFree: false,
      price: 139,
      color: '#795548',
      icon: 'cube-outline',
      progress: 0,
      category: 'Materials',
      description: 'Learn advanced material properties, selection criteria, and processing techniques for engineering applications.',
      modules: [
        { id: '1', title: 'Material Properties', duration: '6 hours' },
        { id: '2', title: 'Selection Methodology', duration: '6 hours' },
        { id: '3', title: 'Processing Techniques', duration: '6 hours' },
        { id: '4', title: 'Failure Analysis', duration: '6 hours' },
      ],
    },
    {
      id: '13',
      title: 'Water Treatment Engineering',
      subtitle: 'Design of water and wastewater treatment systems',
      instructor: 'PE. Sarah Johnson',
      duration: '27 hours',
      rating: 4.7,
      students: 2100,
      level: 'Intermediate',
      isFree: false,
      price: 159,
      color: '#00BCD4',
      icon: 'water',
      progress: 0,
      category: 'Environmental',
      description: 'Professional water and wastewater treatment system design including process selection and plant layout.',
      modules: [
        { id: '1', title: 'Water Quality Parameters', duration: '6 hours' },
        { id: '2', title: 'Treatment Processes', duration: '7 hours' },
        { id: '3', title: 'Plant Design', duration: '7 hours' },
        { id: '4', title: 'Regulatory Compliance', duration: '7 hours' },
      ],
    },
    {
      id: '14',
      title: 'Project Management Professional (PMP)',
      subtitle: 'PMI certification preparation',
      instructor: 'PMP, Mark Thompson',
      duration: '40 hours',
      rating: 4.9,
      students: 5600,
      level: 'Advanced',
      isFree: false,
      price: 299,
      color: '#FF9800',
      icon: 'clipboard-check',
      progress: 0,
      category: 'All',
      description: 'Complete PMP certification preparation covering all knowledge areas and process groups.',
      modules: [
        { id: '1', title: 'Project Integration', duration: '8 hours' },
        { id: '2', title: 'Scope and Schedule', duration: '8 hours' },
        { id: '3', title: 'Cost and Quality', duration: '8 hours' },
        { id: '4', title: 'Risk and Procurement', duration: '8 hours' },
        { id: '5', title: 'Stakeholder Management', duration: '8 hours' },
      ],
    },
    {
      id: '15',
      title: 'Industrial Automation',
      subtitle: 'PLC programming and industrial robotics',
      instructor: 'Automation Engineer',
      duration: '29 hours',
      rating: 4.8,
      students: 3200,
      level: 'Intermediate',
      isFree: false,
      price: 189,
      color: '#607D8B',
      icon: 'robot-industrial',
      progress: 0,
      category: 'Mechanical',
      description: 'Learn PLC programming, industrial robotics, and automation system design for manufacturing.',
      modules: [
        { id: '1', title: 'PLC Fundamentals', duration: '7 hours' },
        { id: '2', title: 'Ladder Logic Programming', duration: '7 hours' },
        { id: '3', title: 'Industrial Robotics', duration: '8 hours' },
        { id: '4', title: 'System Integration', duration: '7 hours' },
      ],
    },
  ];

  const handleSearch = () => {
    if (searchQuery.trim()) {
      Alert.alert('Search', `Searching for "${searchQuery}"...`);
      // In a real app, this would search the course database
    }
  };

  const handleSort = () => {
    const sortOptions = [
      { label: 'Title (A-Z)', value: 'title' },
      { label: 'Rating (High to Low)', value: 'rating' },
      { label: 'Price (Low to High)', value: 'price' },
      { label: 'Duration (Short to Long)', value: 'duration' },
      { label: 'Free First', value: 'freeFirst' },
      { label: 'Popularity (Students)', value: 'students' },
    ];

    Alert.alert(
      'Sort Courses',
      'Choose sorting option:',
      [
        ...sortOptions.map(option => ({
          text: option.label,
          onPress: () => setSortBy(option.value)
        })),
        { text: 'Cancel', style: 'cancel' }
      ]
    );
  };

  const handleFilter = () => {
    Alert.alert(
      'Filter Options',
      'Choose filter criteria:',
      [
        { text: 'All Courses', onPress: () => setActiveTab('All') },
        { text: 'Free Only', onPress: () => filterByPrice('free') },
        { text: 'Paid Only', onPress: () => filterByPrice('paid') },
        { text: 'Beginner Level', onPress: () => filterByLevel('Beginner') },
        { text: 'Intermediate Level', onPress: () => filterByLevel('Intermediate') },
        { text: 'Advanced Level', onPress: () => filterByLevel('Advanced') },
        { text: 'Under £100', onPress: () => filterByPriceRange('under100') },
        { text: '£100-£200', onPress: () => filterByPriceRange('midRange') },
        { text: 'Over £200', onPress: () => filterByPriceRange('over200') },
        { text: 'Cancel', style: 'cancel' }
      ]
    );
  };

  const filterByPrice = (type) => {
    // This would set a price filter state in a real implementation
    Alert.alert('Filter Applied', `Showing ${type} courses only`);
  };

  const filterByLevel = (level) => {
    // This would set a level filter state in a real implementation
    Alert.alert('Filter Applied', `Showing ${level} level courses only`);
  };

  const filterByPriceRange = (range) => {
    const rangeMap = {
      'under100': 'Under £100',
      'midRange': '£100-£200',
      'over200': 'Over £200'
    };
    Alert.alert('Filter Applied', `Showing courses ${rangeMap[range]}`);
  };

  const renderCourseCard = ({ item }) => (
    <TouchableOpacity
      style={styles.courseCard}
      onPress={() =>
        navigation.navigate('CourseDetails', { courseId: item.id, course: item })
      }
    >
      <View
        style={[styles.courseIconContainer, { backgroundColor: item.color }]}
      >
        <MaterialCommunityIcons name={item.icon} size={40} color="#ffffff" />
        {item.isFree && (
          <View style={styles.freeBadge}>
            <Text style={styles.freeBadgeText}>FREE</Text>
          </View>
        )}
      </View>
      <View style={styles.courseInfo}>
        <Text style={styles.courseTitle} numberOfLines={2}>
          {item.title}
        </Text>
        <Text style={styles.courseSubtitle} numberOfLines={1}>
          {item.subtitle}
        </Text>
        <Text style={styles.courseInstructor} numberOfLines={1}>
          👤 {item.instructor}
        </Text>
        <View style={styles.ratingContainer}>
          <FontAwesome5 name="star" size={12} color="#fdb913" />
          <Text style={styles.ratingText}>
            {item.rating} ({item.students.toLocaleString()})
          </Text>
        </View>
        <View style={styles.metaContainer}>
          <Text style={styles.levelText}>{item.level}</Text>
          <Text style={styles.durationText}>{item.duration}</Text>
        </View>
        <View style={styles.priceContainer}>
          <Text style={[styles.priceText, item.isFree && styles.freePriceText]}>
            {item.isFree ? 'Free' : `£${item.price}`}
          </Text>
          {!item.isFree && (
            <View style={styles.categoryBadge}>
              <Text style={styles.categoryBadgeText}>{item.category}</Text>
            </View>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderCategoryItem = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.categoryItem,
        activeTab === item.id && styles.activeCategoryItem,
      ]}
      onPress={() => setActiveTab(item.id)}
    >
      <Text
        style={[
          styles.categoryText,
          activeTab === item.id && styles.activeCategoryText,
        ]}
      >
        {item.name}
      </Text>
    </TouchableOpacity>
  );

  // Fixed filtering logic
  const filteredCourses = courses.filter(course => {
    // Category filter
    const matchesCategory = activeTab === 'All' || course.category === activeTab;
    
    // Search filter
    const matchesSearch = searchQuery === '' || 
      String(course.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(course.subtitle ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(course.instructor ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(course.category ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(course.description ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  }).sort((a, b) => {
    // Sorting logic
    switch (sortBy) {
      case 'rating':
        return b.rating - a.rating;
      case 'price':
        return a.price - b.price;
      case 'duration':
        return parseInt(a.duration) - parseInt(b.duration);
      case 'freeFirst':
        if (a.isFree && !b.isFree) return -1;
        if (!a.isFree && b.isFree) return 1;
        return 0;
      case 'students':
        return b.students - a.students;
      case 'title':
      default:
        return a.title.localeCompare(b.title);
    }
  });

  const renderHeader = () => (
    <View>
      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Ionicons
          name="search"
          size={20}
          color="#64748B"
          style={styles.searchIcon}
        />
        <TextInput
          style={styles.searchInput}
          placeholder="Search courses, instructors, topics..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          onSubmitEditing={handleSearch}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={20} color="#64748B" />
          </TouchableOpacity>
        )}
      </View>

      {/* Course Stats */}
      <View style={styles.statsContainer}>
        <View style={styles.statItem}>
          <Text style={styles.statNumber}>{courses.length}</Text>
          <Text style={styles.statLabel}>Total Courses</Text>
        </View>
        <View style={styles.statDivider} />
        <View style={styles.statItem}>
          <Text style={styles.statNumber}>4</Text>
          <Text style={styles.statLabel}>Free Courses</Text>
        </View>
        <View style={styles.statDivider} />
        <View style={styles.statItem}>
          <Text style={styles.statNumber}>11</Text>
          <Text style={styles.statLabel}>Paid Courses</Text>
        </View>
      </View>

      {/* Categories */}
      <View style={styles.categoriesContainer}>
        <FlatList
          data={categories}
          renderItem={renderCategoryItem}
          keyExtractor={item => item.id}
          horizontal
          showsHorizontalScrollIndicator={false}
          nestedScrollEnabled={true}
        />
      </View>

      {/* Results Header */}
      <View style={styles.resultsHeader}>
        <Text style={styles.resultsText}>
          {filteredCourses.length} courses found
          {activeTab !== 'All' && ` in ${activeTab}`}
          {searchQuery && ` for "${searchQuery}"`}
        </Text>
        <View style={styles.actionButtons}>
          <TouchableOpacity style={styles.filterButton} onPress={handleFilter}>
            <Ionicons name="filter" size={16} color="#0e4e96" />
            <Text style={styles.filterText}>Filter</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.sortButton} onPress={handleSort}>
            <Ionicons name="swap-vertical" size={16} color="#0e4e96" />
            <Text style={styles.sortText}>Sort</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  // Create pairs of courses for two-column layout
  const coursePairs = [];
  for (let i = 0; i < filteredCourses.length; i += 2) {
    coursePairs.push({
      id: `pair-${i}`,
      courses: [
        filteredCourses[i],
        filteredCourses[i + 1] || null
      ]
    });
  }

  const renderCoursePair = ({ item }) => (
    <View style={styles.row}>
      {renderCourseCard({ item: item.courses[0] })}
      {item.courses[1] && renderCourseCard({ item: item.courses[1] })}
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="dark-content" backgroundColor="#f0f4f8" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color="#0e4e96" />
        </TouchableOpacity>
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>Engineering Courses</Text>
          <Text style={styles.headerSubtitle}>15 courses available</Text>
        </View>
        <TouchableOpacity style={styles.filterButton} onPress={handleFilter}>
          <Ionicons name="options-outline" size={24} color="#0e4e96" />
        </TouchableOpacity>
      </View>

      {/* Main Content - Single FlatList */}
      <FlatList
        data={coursePairs}
        renderItem={renderCoursePair}
        keyExtractor={item => item.id}
        ListHeaderComponent={renderHeader}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={() => (
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="book-search" size={64} color="#64748B" />
            <Text style={styles.emptyTitle}>No courses found</Text>
            <Text style={styles.emptyText}>
              Try adjusting your search or filter criteria
            </Text>
            <TouchableOpacity 
              style={styles.clearButton} 
              onPress={() => {
                setSearchQuery('');
                setActiveTab('All');
              }}
            >
              <Text style={styles.clearButtonText}>Clear All Filters</Text>
            </TouchableOpacity>
          </View>
        )}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 5,
  },
  headerTitleContainer: {
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  headerSubtitle: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 2,
  },
  filterButton: {
    padding: 5,
  },
  contentContainer: {
    padding: 15,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 10,
    paddingHorizontal: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  searchIcon: {
    marginRight: 10,
  },
  searchInput: {
    flex: 1,
    height: 40,
    fontSize: 16,
    color: '#333',
  },
  statsContainer: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 2,
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  statLabel: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 4,
  },
  statDivider: {
    width: 1,
    height: '80%',
    backgroundColor: '#e0e0e0',
    marginHorizontal: 10,
  },
  categoriesContainer: {
    marginBottom: 15,
  },
  categoryItem: {
    backgroundColor: '#e9eef4',
    borderRadius: 20,
    paddingVertical: 8,
    paddingHorizontal: 15,
    marginRight: 10,
  },
  activeCategoryItem: {
    backgroundColor: '#0e4e96',
  },
  categoryText: {
    color: '#0e4e96',
    fontWeight: 'bold',
    fontSize: 14,
  },
  activeCategoryText: {
    color: '#fff',
  },
  resultsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  resultsText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    flex: 1,
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    marginRight: 8,
  },
  filterText: {
    fontSize: 14,
    color: '#0e4e96',
    marginLeft: 4,
    fontWeight: '500',
  },
  sortButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  sortText: {
    fontSize: 14,
    color: '#0e4e96',
    marginLeft: 4,
    fontWeight: '500',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  courseCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    width: '48%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
    overflow: 'hidden',
  },
  courseIconContainer: {
    width: '100%',
    height: 100,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  freeBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: '#4CAF50',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  freeBadgeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  courseInfo: {
    padding: 10,
  },
  courseTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
    lineHeight: 20,
  },
  courseSubtitle: {
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
  },
  courseInstructor: {
    fontSize: 11,
    color: '#64748B',
    marginBottom: 6,
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  ratingText: {
    fontSize: 12,
    color: '#fdb913',
    marginLeft: 5,
    fontWeight: '600',
  },
  metaContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  levelText: {
    fontSize: 11,
    color: '#0e4e96',
    fontWeight: 'bold',
    backgroundColor: '#e9eef4',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  durationText: {
    fontSize: 11,
    color: '#666',
  },
  priceContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  priceText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#19abb5',
  },
  freePriceText: {
    color: '#4CAF50',
  },
  categoryBadge: {
    backgroundColor: '#f0f4f8',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  categoryBadgeText: {
    fontSize: 10,
    color: '#64748B',
    fontWeight: '600',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 24,
  },
  clearButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  clearButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default LearningCoursesScreen;